import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const LoaderContext = createContext(null);

/** Delay before showing full-screen loader (avoids flash on fast / parallel API calls). */
const LOADER_SHOW_DELAY_MS = 220;

export const LoaderProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const pendingRef = useRef(0);
  const showTimerRef = useRef(null);

  const clearShowTimer = useCallback(() => {
    if (showTimerRef.current != null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  }, []);

  const startRequest = useCallback(() => {
    pendingRef.current += 1;
    if (pendingRef.current === 1) {
      clearShowTimer();
      showTimerRef.current = window.setTimeout(() => {
        setLoading(true);
        showTimerRef.current = null;
      }, LOADER_SHOW_DELAY_MS);
    }
  }, [clearShowTimer]);

  const endRequest = useCallback(() => {
    pendingRef.current = Math.max(0, pendingRef.current - 1);
    if (pendingRef.current === 0) {
      clearShowTimer();
      setLoading(false);
    }
  }, [clearShowTimer]);

  /** Legacy boolean setter — prefer startRequest / endRequest via axios. */
  const setLoadingLegacy = useCallback(
    (value) => {
      if (value) startRequest();
      else endRequest();
    },
    [startRequest, endRequest]
  );

  useEffect(
    () => () => {
      clearShowTimer();
      pendingRef.current = 0;
    },
    [clearShowTimer]
  );

  return (
    <LoaderContext.Provider
      value={{
        loading,
        setLoading: setLoadingLegacy,
        startRequest,
        endRequest,
      }}
    >
      {children}
    </LoaderContext.Provider>
  );
};

export const useLoader = () => {
  const ctx = useContext(LoaderContext);
  if (!ctx) {
    throw new Error("useLoader must be used within LoaderProvider");
  }
  return ctx;
};
